"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { readQuantity } from "@/lib/tools/numeric-input"
import type { ToolComponentData } from "@/lib/tool-adapter"

// Standard amortization formula:
//   monthly = P × r(1+r)^n / ((1+r)^n − 1)
// where P = principal, r = monthly rate (APR/12), n = months.
// We render a side-by-side table comparing standard APR to 0% promo APR.

const NATIONAL_AVG_APR = 11.5
const TERMS = [12, 24, 36, 48, 60] as const

function calcPayment(principal: number, annualRate: number, months: number): number {
  if (annualRate === 0) return principal / months
  const r = annualRate / 100 / 12
  return (principal * (r * Math.pow(1 + r, months))) / (Math.pow(1 + r, months) - 1)
}

const RELATED = [
  {
    title: "Refrigerator Financing Calculator",
    category: "Energy & Cost",
    description: "Custom APR + term, full amortization breakdown.",
    href: "/tools/refrigerator-financing-calculator",
    time: "~30 sec",
  },
  {
    title: "Financing Comparison Tool",
    category: "Specialty",
    description: "Side-by-side promo vs standard offers.",
    href: "/tools/financing-comparison-tool",
    time: "~45 sec",
  },
  {
    title: "Refrigerator Cost of Ownership Calculator",
    category: "Energy & Cost",
    description: "True 10-year purchase + energy + maintenance cost.",
    href: "/tools/refrigerator-cost-of-ownership-calculator",
    time: "~45 sec",
  },
  {
    title: "Refrigerator Rebate Finder",
    category: "Energy & Cost",
    description: "Up to $500 back on ENERGY STAR upgrades.",
    href: "/tools/refrigerator-rebate-finder",
    time: "~30 sec",
  },
]

export function RefrigeratorPaymentEstimatorClient(_props: { toolData?: ToolComponentData } = {}) {
  const [price, setPrice] = useState("1800")

  const result = useMemo(() => {
    // Bounds unchanged; out-of-range input is now rejected rather than clamped.
    // `safeParseNumber(price, 1, 50000)` returned 1 for a typed -1800, which is
    // truthy — so the guard could not fire and the estimator quoted a monthly
    // payment on a $1 refrigerator. FRIDGE-384 / FRIDGE-411.
    const p = readQuantity(price, { min: 1, max: 50000 })
    if (p === null) return null

    const standard = TERMS.map((months) => {
      const monthly = calcPayment(p, NATIONAL_AVG_APR, months)
      const total = monthly * months
      const interest = total - p
      return { months, monthly, total, interest }
    })
    const zeroApr = TERMS.map((months) => ({
      months,
      monthly: p / months,
      total: p,
      interest: 0,
    }))

    return {
      principal: p,
      apr: NATIONAL_AVG_APR,
      standard,
      zeroApr,
      lowest: standard[0].monthly === Infinity ? null : standard[standard.length - 1],
    }
  }, [price])

  return (
    <ToolPageShell
      toolName="Refrigerator Payment Estimator"
      heroEyebrow="Energy & Cost"
      heroTitlePre="A quick monthly payment"
      heroTitleItalic="for any"
      heroTitlePost="fridge price."
      heroSubtitle={`Enter the price; we run the standard amortization formula at the national-average personal-loan APR (${NATIONAL_AVG_APR}%) across all common terms — plus a side-by-side 0% APR view.`}
      heroMeta={["⏱ ~15 sec", `National avg APR: ${NATIONAL_AVG_APR}%`]}
      heroShare={<ToolShareBar title="Refrigerator Payment Estimator" />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: `${NATIONAL_AVG_APR}%`, label: "National avg personal loan APR", note: "Reference rate" },
            { value: "P×r(1+r)ⁿ", label: "Standard amortization", note: "÷ ((1+r)ⁿ − 1)" },
            { value: "5", label: "Terms compared", note: "12 / 24 / 36 / 48 / 60 mo" },
            { value: "0%", label: "Promo APR side-by-side", note: "Compare offers" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: `Why ${NATIONAL_AVG_APR}% APR?`,
          answer:
            `${NATIONAL_AVG_APR}% is a reasonable reference for the national average personal-loan APR. Your actual rate depends on credit score, lender, and whether you use store financing, a credit card, or a personal loan — get the offer in writing first.`,
        },
        {
          question: "What if I'm offered 0% APR?",
          answer:
            "The right column shows 0% APR for the same price across all terms — equal monthly payments, no interest. Useful for comparing a promo to standard financing.",
        },
        {
          question: "Why is the same price so different month-to-month across terms?",
          answer:
            "Longer terms spread the principal over more payments, so each individual payment shrinks — but you pay more interest in total. 12 months has the highest monthly payment but the lowest total interest.",
        },
        {
          question: "Should I include sales tax and delivery?",
          answer:
            "If those will be financed, include them in the price. If you're paying them up front, leave them out so the math reflects only what's being financed.",
        },
        {
          question: "Is this estimate exact?",
          answer:
            "It's the standard amortization math every lender uses. Your actual loan may include origination fees, prepayment terms, or insurance — read the loan disclosure carefully.",
        },
      ]}
      relatedTools={RELATED}
      cta={{
        title: "See models priced where the math works",
        body: "Browse bottom-freezer refrigerators sorted by price, capacity, and live retailer pricing.",
        buttonLabel: "Browse bottom-freezer refrigerators",
        buttonHref: "/collections/bottom-freezer-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                One number.
                <br />
                Five terms.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                We run the math at the national-average APR and at 0% — you decide.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Refrigerator price
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[18px] font-bold text-gray-500">$</span>
                    <input
                      type="number"
                      step="50"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="tool-input-clean w-full pl-9 pr-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Include tax + delivery if financed
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      60-month payment @ {NATIONAL_AVG_APR}%
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {`$${result?.lowest?.monthly.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "0.00"}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      /mo
                    </span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result
                      ? `Lowest monthly payment shown — total interest at this term is $${result.lowest?.interest.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? 0}. See full term comparison below.`
                      : "Enter a price to see payment estimates."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Principal
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.principal.toLocaleString() ?? "—"}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Reference APR
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {NATIONAL_AVG_APR}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Term comparison table */}
          {result && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-7">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-gray-900">
                All terms · standard APR vs 0% promo
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left" style={{ fontVariantNumeric: "tabular-nums" }}>
                  <thead>
                    <tr className="border-b border-gray-300 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold">
                      <th className="py-3 pr-4">Term</th>
                      <th className="py-3 pr-4">Monthly @ {NATIONAL_AVG_APR}%</th>
                      <th className="py-3 pr-4">Total interest</th>
                      <th className="py-3 pr-4">Monthly @ 0% promo</th>
                      <th className="py-3 pr-4">Total cost @ 0%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.standard.map((row, i) => (
                      <tr key={row.months} className="border-b border-gray-100 text-[14px]">
                        <td className="py-3 pr-4 font-bold text-gray-900">{row.months} mo</td>
                        <td className="py-3 pr-4 text-gray-900">{`$${row.monthly.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}/mo</td>
                        <td className="py-3 pr-4 text-gray-700">{`$${row.interest.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</td>
                        <td className="py-3 pr-4 text-gray-900">{`$${result.zeroApr[i].monthly.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}/mo</td>
                        <td className="py-3 pr-4 text-gray-700">{`$${result.zeroApr[i].total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · See real prices"
          title="Bottom-freezer refrigerators"
          description="Bend less, see more — and a strong value tier at every price point."
          collectionType="bottom-freezer"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Standard amortization, <em className="italic text-[#ff9951]">two ways</em>.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                We compute the same payment at the national-average personal-loan APR and again at 0% APR
                so you can compare a promo offer directly against a typical loan.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Standard amortization at national-average APR",
                  d: `monthly = P × r(1+r)ⁿ / ((1+r)ⁿ − 1), with r = ${NATIONAL_AVG_APR}% ÷ 12 and n = months.`,
                },
                {
                  n: "02",
                  t: "0% APR side-by-side",
                  d: "When APR = 0, monthly = P ÷ months. Equal payments, no interest, total = principal.",
                },
                {
                  n: "03",
                  t: "Total interest = total cost − principal",
                  d: "We multiply the monthly payment by months and subtract the price to isolate what you actually pay in interest.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth refrigerators"
          description="Built-in look, full-size capacity, premium energy efficiency."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
